import * as React from "react";
import { useEffect, useState } from "react";
import { useHistory, useRouteMatch } from "react-router";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { ClinicLoginWidget } from "../clinicLoginWidget";
import { ClinicOnboardView } from "./clinicOnboardView";
import { getCurrentUserEmail } from "user-management/src/cognito/kahunUserAuth";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { CircularProgress, MenuItem, Select, styled } from "@mui/material";
import { ClinicFormInput } from "../elements/clinicFormInput";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { CLINIC_MANAGE_URL } from "../clinicSettings/clinicUrls";
import { ClinicConfig } from "fe-common/graphql/clinic/clinicTypes";
import { MASTER_CLINIC_ID } from "fe-common/commonTypes/masterClinicDefaults";
import { sendMessageWithCommunicationService } from "../util/sendMessageWithCommmunicationService";
import { SupportedCommunicationPlatforms } from "fe-common/commonTypes/communicationLambdaApi";
import { MessageTouchPoint } from "@kahunmedical/common-configuration/dist/configuration/messageTemplate";

//region [[ Styles ]]
const StatusIconContainer = styled("div")({
  width: "18px",
  height: "18px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&.error svg": {
    fill: "lightpink",
  },
  "&.success svg": {
    fill: "lightgreen",
  },
});

const Footer = styled("div")({
  textAlign: "center",
});

const FormContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: 12,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ClinicCreateProps {
  clinicId?: string;
  onSubmit?: (id, newWebName, newDisplayName) => void;
  inline?: boolean;
  errorText?: string;
  label?: string;
}

export interface SubscriptionOptions {
  contactName?: string;
  contactEmail?: string;
  organizationName?: string;
  numberOfPrimaryCare?: string;
  ehrSystem?: string;
}

const EHRSystems = [
  "Athenahealth",
  "eClinicalWorks",
  "Epic",
  "Cerner",
  "Nextgen",
  "GE",
  "Allscripts",
  "Greenway Health",
  "e-MDs",
  "Meditech",
  "Practice Fusion",
  "Proprietary",
];

const MAX_CLINIC_NAME_LENGTH = 32;
//endregion [[ Props ]]

//endregion [[ Functions ]]

export const ClinicCreate = ({ ...props }: ClinicCreateProps) => {
  const dao = useClinicContext();
  const history = useHistory();
  const match = useRouteMatch<{ clinicId: string }>();
  const [name, setName] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [clinicSubscriptionOptions, setClinicSubscriptionOptions] = useState<
    SubscriptionOptions
  >({});
  const [otherEHR, setOtherEHR] = useState<string>("");
  const [errorText, setErrorText] = useState<string>(null);
  const [loading, setLoading] = useState(false);
  const [checkingUrlName, setCheckingUrlName] = useState(false);
  const [nameIsValid, setNameIsValid] = useState(true);

  const onChangeClinicName = (e) => {
    const newDisplayName = e.target.value;
    setDisplayName(newDisplayName);

    if (newDisplayName.length > MAX_CLINIC_NAME_LENGTH) {
      setNameIsValid(false);
      setErrorText(`Clinic name exceeds ${MAX_CLINIC_NAME_LENGTH} characters`);
      return;
    }
    setName(
      encodeURIComponent(newDisplayName.trim().toLowerCase()).replaceAll(
        "%20",
        "_",
      ),
    );
  };

  const createNewClinic = async () => {
    if (!name || name === "" || !displayName || displayName === "") {
      setErrorText("Please fill in a display name");
      return;
    }
    let initialClinicSettings = {
      unifiedConfigString: {
        "clinic.basedOnClinic": MASTER_CLINIC_ID, //DEFAULT MASTER CLINIC
      } as any,
    };
    if (match.params?.clinicId) {
      try {
        const clinicData = await dao.clinicData(match.params.clinicId);
        if (clinicData) {
          initialClinicSettings = clinicData.config as any;
        }
      } catch (e) {}
    }

    setErrorText(null);
    setLoading(true);
    let clinicId = null;
    const currentUser = await getCurrentUserEmail();
    //analytics
    evt({
      category: CATEGORY.CLINIC_MANAGEMENT,
      action: ACTION.CLINIC_CREATED,
      props: {
        clinicName: displayName,
        userId: currentUser,
        ...clinicSubscriptionOptions,
      },
    });
    const subscriptionOptions = {
      "backoffice.subscriptionOptions.contactEmail": currentUser,
    };
    if (clinicSubscriptionOptions.organizationName)
      subscriptionOptions["backoffice.subscriptionOptions.organizationName"] =
        clinicSubscriptionOptions.organizationName;
    if (clinicSubscriptionOptions.ehrSystem !== "-1" || otherEHR)
      subscriptionOptions["backoffice.subscriptionOptions.ehrSystem"] =
        clinicSubscriptionOptions.ehrSystem === "other"
          ? otherEHR
          : clinicSubscriptionOptions.ehrSystem;
    if (clinicSubscriptionOptions.numberOfPrimaryCare)
      subscriptionOptions[
        "backoffice.subscriptionOptions.numberOfPrimaryCare"
      ] = clinicSubscriptionOptions.numberOfPrimaryCare;

    try {
      const res = await dao.createClinic(name, displayName, {
        ...initialClinicSettings,
        unifiedConfigString: {
          ...initialClinicSettings.unifiedConfigString,
          ...subscriptionOptions,
        },
      } as ClinicConfig);
      clinicId = res.id;

      //send welcome email
      await sendMessageWithCommunicationService(
        name,
        SupportedCommunicationPlatforms.Email,
        {
          to: currentUser,
          touchPoint: MessageTouchPoint.SendNewUserInvitation,
          macroFields: {
            clinic_name: res.name,
            clinic_display_name: res.displayName,
            clinic_id: clinicId,
          },
        },
      );
      // await sendEmailWithEmailJs({
      //   template_params: {
      //     clinic_name: res.displayName,
      //     contact_name: currentUser,
      //     contact_email: currentUser,
      //     clinic_id: res.id,
      //   },
      //   template_id: emailKeys.EMAIL_JS_ADD_USER_TO_CLINIC_TEMPLATE_ID,
      //   user_id: emailKeys.EMAIL_JS_USER_ID,
      //   service_id: emailKeys.EMAIL_JS_NOREPLY_SERVICE_ID,
      // });
      //send notification for a new clinic creation
      await sendMessageWithCommunicationService(
        name,
        SupportedCommunicationPlatforms.Email,
        {
          to: null,
          touchPoint: MessageTouchPoint.NewClinicCreated,
          macroFields: {
            clinic_id: clinicId,
            clinic_name: res.displayName,
            contact_name: clinicSubscriptionOptions.contactName || "",
            contact_email: currentUser,
            organization_name: clinicSubscriptionOptions.organizationName,
            number_of_physicians: clinicSubscriptionOptions.numberOfPrimaryCare,
            ehr_system: clinicSubscriptionOptions.ehrSystem,
          },
        },
      );
    } catch (e) {
      console.error(e);
      clinicId = null;
      setErrorText("Failed to create a clinic");
    } finally {
      if (clinicId) history.push(`${CLINIC_MANAGE_URL}/${clinicId}`);
      setLoading(false);
    }
  };

  const onChangeSubscriptionOptions = (field: string, value: string) => {
    setClinicSubscriptionOptions((prevState) => {
      const newState = { ...prevState };
      newState[field] = value;
      return newState;
    });
  };

  useEffect(() => {
    setErrorText(null);
    setNameIsValid(true);
    if (!name || !name.trim()) return;

    setCheckingUrlName(true);
    dao.isClinicWebNameAvailable(name).then((res) => {
      setCheckingUrlName(false);
      if (res) {
        setErrorText(null);
        setNameIsValid(true);
      } else {
        setNameIsValid(false);
        setErrorText("Clinic name is already taken. Please try another name.");
      }
    });
  }, [dao, name]);

  return (
    <ClinicOnboardView
      title={match.params.clinicId ? "Duplicate clinic" : "Create Your Clinic"}
    >
      <ClinicLoginWidget absolute />
      <form>
        <FormContainer>
          <ClinicFormInput
            label={"Clinic Name"}
            id={"clinicName"}
            value={displayName}
            onChange={onChangeClinicName}
            error={errorText}
            placeHolder={"Enter a name"}
            inputSuffix={
              displayName &&
              (checkingUrlName ? (
                <CircularProgress size={16} />
              ) : (
                <StatusIconContainer
                  className={nameIsValid ? "success" : "error"}
                >
                  {nameIsValid ? <CheckCircleIcon /> : <HighlightOffIcon />}
                </StatusIconContainer>
              ))
            }
          />
          <ClinicFormInput
            label={"Organization Name"}
            value={clinicSubscriptionOptions?.organizationName || ""}
            onChange={(e) =>
              onChangeSubscriptionOptions("organizationName", e.target.value)
            }
          />
          <ClinicFormInput
            label={"EHR System"}
            id={"ehrSystem"}
            customInput={
              <Select
                id="defaultLanguageSelect"
                value={clinicSubscriptionOptions?.ehrSystem || "-1"}
                variant={"outlined"}
                onChange={(e) =>
                  onChangeSubscriptionOptions(
                    "ehrSystem",
                    e.target.value as string,
                  )
                }
              >
                <MenuItem value={"-1"} key={"ehrSystemNull"}>
                  Select
                </MenuItem>
                {EHRSystems.map((ehrSystem, i) => (
                  <MenuItem value={ehrSystem} key={"ehrSystem" + i}>
                    {ehrSystem}
                  </MenuItem>
                ))}
                <MenuItem value={"other"} key={"ehrSystemOther"}>
                  Other
                </MenuItem>
              </Select>
            }
          />
          {clinicSubscriptionOptions?.ehrSystem === "other" && (
            <ClinicFormInput
              label=""
              id="otherEHR"
              value={otherEHR}
              onChange={(e) => {
                setOtherEHR(e.target.value);
              }}
              placeHolder={"Enter EHR Name"}
            />
          )}
          <ClinicFormInput
            label={"# of Primary/Urgent Care Physicians"}
            value={clinicSubscriptionOptions?.numberOfPrimaryCare || ""}
            onChange={(e) =>
              onChangeSubscriptionOptions("numberOfPrimaryCare", e.target.value)
            }
          />
        </FormContainer>
        <Footer>
          <ClinicActionButton
            className="submit-button"
            onClick={createNewClinic}
            label={props.label || "Create"}
            disabled={
              loading || checkingUrlName || !displayName || !nameIsValid
            }
            endIcon={loading && <CircularProgress size={20} />}
          />
        </Footer>
      </form>
    </ClinicOnboardView>
  );
};
